// The exchange's state in words, from the server's NSE calendar (holidays and Muhurat included).
import type { MarketClockState } from '@/lib/client/use-market';

const hm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** When the next session opens: "opens 09:15" (later today), "opens tomorrow", or a weekday, IST. */
export function opensText(nextOpen: string | undefined, now: Date) {
  if (!nextOpen) return '';
  const day = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const n = new Date(nextOpen);
  if (day(n) === day(now)) return 'opens 09:15';
  if (day(n) === day(new Date(now.getTime() + 86_400_000))) return 'opens tomorrow';
  return `opens ${n.toLocaleDateString('en-IN', { weekday: 'short', timeZone: 'Asia/Kolkata' })}`;
}

/** A special session on a holiday (Muhurat trading) in words, or ''. */
function specialText(c: MarketClockState) {
  if (!c.special) return '';
  return c.special.open > 0 ? `${c.special.name} ${hm(c.special.open)}–${hm(c.special.close)}` : `${c.special.name} (hours to be announced)`;
}

/** "Open · closes 15:30", "Holiday · Dussehra · opens tomorrow", "Closed · opens Mon". */
export function clockText(c: MarketClockState, now: Date) {
  const sp = specialText(c);
  switch (c.state) {
    case 'open': return 'Open · closes 15:30';
    case 'pre': return 'Opens 09:15';
    case 'special': return `${c.special?.name ?? 'Special session'} · until ${hm(c.special?.close ?? 0)}`;
    case 'holiday': return `Holiday · ${c.holiday}${sp ? ` · ${sp}` : ''} · ${opensText(c.nextOpen, now)}`;
    default: return `Closed · ${opensText(c.nextOpen, now)}`;
  }
}

/** A holiday's details for its tooltip: why it's closed, any special session, when it opens, and
 * which session the app is showing. */
export function holidayInfo(c: MarketClockState, now: Date, session?: string | null) {
  const parts = [`NSE is closed today for ${c.holiday ?? 'a holiday'}.`];
  const sp = specialText(c);
  if (sp) parts.push(`Special session: ${sp}.`);
  const opens = opensText(c.nextOpen, now);
  if (opens) parts.push(`It ${opens === 'opens 09:15' ? 'opens at 09:15' : `${opens} at 09:15`}.`);
  if (session) {
    const d = new Date(`${session}T00:00:00+05:30`);
    parts.push(`Showing the last session, ${d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' })}.`);
  }
  return parts.join(' ');
}

/** Whether the exchange is shut for the whole day (a holiday or a weekend). */
export const shutToday = (c?: MarketClockState | null) => c?.state === 'holiday' || c?.state === 'weekend';
